import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "react-query";
import { useDispatch } from "react-redux";
import {
  fetchUserWill,
  getAllPlans,
  getPlanByType,
  getUserPlanDetails,
  saveBasicInfo,
  saveFamilyInfo,
  savePetGuardian,
  saveAltPetGuardian,
  saveChildGuardian,
  saveAltChildGuardian,
  saveDistribution,
  saveExecutor,
  savePoaAttorney,
  savePoaCare,
  getPaymentStatus,
  downloadWillPdf,
  downloadPoaCarePdf,
  downloadPoaPropertyPdf,
  notifyContact,
  refreshToken,
} from "../../services";
import StoreTypes from "../../store/types";
import { sanitizePayload } from "../../utils/sanitizer";
import storage from "../../utils/storage";

function mapDetails(data, metadata) {
  const userContext = metadata?.userContext;
  return {
    basic: data?.basicInfo,
    family: data?.family,
    distribution: data?.distribution,
    executor: data?.executor,
    guardian: {
      petGuardian: data?.petGuardian,
      alternatePetGuardian: data?.alternatePetGuardian,
      childGuardian: data?.childGuardian,
      alternateChildGuardian: data?.alternateChildGuardian,
    },
    poa: data?.poaAttorney,
    poaCare: data?.poaCare,
    userContext,
    metadata: {
      overallProgress: metadata?.overallProgress,
      isFullyComplete: metadata?.isFullyComplete,
      requiredSections: metadata?.requiredSections,
      sectionStatus: metadata?.sectionStatus,
      userContext,
      isEmailVerified: metadata?.isEmailVerified,
      accountDeletionStatus: data?.deletionStatus,
      deletionScheduledFor: data?.deletionTokenExpiry,
    },
  };
}

const mapPlanToCard = (plan) => ({
  ...plan,
  koboPrice: plan.price,
  koboUpgradePrice: plan.upgradePrice,
  title: plan.name || plan.title,
  price: plan.priceFormatted || plan.price,
  subTitle:
    plan.id === "will_and_poa" ? "Will + Powers of Attorney" : "Last Will",
  description:
    plan.description ||
    (plan.id === "will_and_poa"
      ? "Complete estate planning package"
      : "Essential will creation"),
  headerBg: plan.id === "will_and_poa" ? "bg-[#47392D]" : "bg-[#DCC39A]",
  cardBg: plan.id === "will_and_poa" ? "bg-[#976A3E]" : "bg-[#FAF4EB]",
  buttonColor:
    plan.id === "will_and_poa"
      ? "bg-white text-[#47392D]"
      : "bg-[#47392D] text-white",
  buttonText: plan.buttonText || "Buy Plan",
  recommended: plan.recommended || false,
  savings: plan.savings || null,
});

const BASIC_INFO_ONLY_FIELDS = [
  "hasPet",
  "hasChildren",
  "hasOtherChildren",
  "incapacityPlan",
];

const useGenericMutation = (mutationFn, options = {}) => {
  const queryClient = useQueryClient();
  const [isSaving, setIsSaving] = useState(false);
  const { mutateAsync } = useMutation((data) =>
    mutationFn(sanitizePayload(data, options.blacklist)),
  );

  const mutate = async (data, mutateOptions = {}) => {
    setIsSaving(true);
    try {
      const response = await mutateAsync(data);
      if (options.onSuccess) {
        options.onSuccess(response);
      }
      if (options.invalidateQueries !== false) {
        await Promise.all([
          queryClient.refetchQueries(["userWill"]),
          queryClient.refetchQueries(["paymentStatus"]),
          queryClient.refetchQueries(["userPlanDetails"]),
        ]);
      }
      if (mutateOptions.onSuccess) {
        mutateOptions.onSuccess(response);
      }
      return response;
    } catch (error) {
      // Prevent unhandled promise rejections from bubbling into the React dev overlay.
      if (options.onError) {
        options.onError(error);
      }
      if (mutateOptions.onError) {
        mutateOptions.onError(error);
      }
      return null;
    } finally {
      setIsSaving(false);
    }
  };

  return { isLoading: isSaving, mutate };
};

export const useGetUserInfoQuery = () => {
  const dispatch = useDispatch();
  const hasToken = !!storage.getToken();
  
  const { isLoading, refetch } = useQuery({
    queryKey: ["userWill"],
    queryFn: () => fetchUserWill(),
    refetchOnWindowFocus: false,
    refetchOnMount: true,
    enabled: hasToken, // Only fetch if user has a token (is authenticated)
    onSuccess: (payload) => {
      const data = payload?.data;
      const metadata = payload?.metadata;
      dispatch({
        type: StoreTypes.SET_DETAILS,
        payload: mapDetails(data, metadata),
      });
    },
  });

  return { isLoading, refetch };
};

export const useGetPlansQuery = (region) => {
  const { isLoading, data } = useQuery({
    queryKey: ["plans", region],
    queryFn: () => getAllPlans(region),
    refetchOnWindowFocus: false,
    refetchOnMount: false,
  });
  const rawPlans =
    data?.data?.availablePlans || data?.data?.plans || data?.data;

  const plans = Array.isArray(rawPlans) ? rawPlans.map(mapPlanToCard) : [];

  return { isLoading, plans };
};

export const useGetPlanByTypeQuery = (planType, region) => {
  const { isLoading, data } = useQuery({
    queryKey: ["plan", planType, region],
    queryFn: () => getPlanByType(planType, region),
    enabled: !!planType,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
  });

  const plan = data?.data ? mapPlanToCard(data.data) : null;

  return { isLoading, plan };
};

export const useGetUserPlanDetailsQuery = () => {
  const { isLoading, data, refetch } = useQuery({
    queryKey: ["userPlanDetails"],
    queryFn: () => getUserPlanDetails(),
    refetchOnWindowFocus: false,
    refetchOnMount: true,
  });

  const planDetails = data?.data;
  const currentPlan = planDetails?.currentPlan;
  const currentPlanId = currentPlan?.id;
  const paymentStatus = planDetails?.paymentStatus;

  const availablePlans = planDetails?.availablePlans || [];
  const upgradeOption = availablePlans.find((p) => p.isUpgrade);

  const displayPlans = availablePlans.map(mapPlanToCard);

  return {
    isLoading,
    planDetails,
    currentPlan,
    currentPlanId,
    paymentStatus,
    availablePlans,
    displayPlans,
    upgradeOption,
    refetch,
  };
};

export const usePaymentStatusQuery = () => {
  const { isLoading, data, refetch } = useQuery({
    queryKey: ["paymentStatus"],
    queryFn: () => getPaymentStatus(),
    refetchOnWindowFocus: true,
    refetchOnMount: true,
    staleTime: 0, // Always consider data stale to get fresh payment status
  });

  return {
    isLoading,
    paymentStatus: data?.data?.paymentStatus || false,
    canAccessWill: data?.data?.canAccessWill || false,
    canAccessPoa: data?.data?.canAccessPoa || false,
    planDetails: data?.data?.planDetails,
    refetch,
  };
};

export const useBasicInfoMutation = () => useGenericMutation(saveBasicInfo);
export const useFamilyInfoMutation = () =>
  useGenericMutation(saveFamilyInfo, { blacklist: BASIC_INFO_ONLY_FIELDS });
export const usePetGuardianMutation = () =>
  useGenericMutation(savePetGuardian, { blacklist: BASIC_INFO_ONLY_FIELDS });
export const useAltPetGuardianMutation = () =>
  useGenericMutation(saveAltPetGuardian, { blacklist: BASIC_INFO_ONLY_FIELDS });
export const useDistributionMutation = () =>
  useGenericMutation(saveDistribution, { blacklist: BASIC_INFO_ONLY_FIELDS });
export const useExecutorMutation = () =>
  useGenericMutation(saveExecutor, { blacklist: BASIC_INFO_ONLY_FIELDS });
export const usePoaAttorneyMutation = () =>
  useGenericMutation(savePoaAttorney, { blacklist: BASIC_INFO_ONLY_FIELDS });
export const useChildGuardianMutation = () =>
  useGenericMutation(saveChildGuardian, { blacklist: BASIC_INFO_ONLY_FIELDS });
export const useAltChildGuardianMutation = () =>
  useGenericMutation(saveAltChildGuardian, {
    blacklist: BASIC_INFO_ONLY_FIELDS,
  });
export const usePoaCareMutation = () =>
  useGenericMutation(savePoaCare, { blacklist: BASIC_INFO_ONLY_FIELDS });

export const useRefreshToken = () => {
  return useGenericMutation(refreshToken, {
    invalidateQueries: false,
    onSuccess: (response) => {
      const token = response?.data?.token;
      if (token) {
        localStorage.setItem("token", token);
      }
    },
  });
};

const handleDownload = (data, filename) => {
  const url = window.URL.createObjectURL(new Blob([data]));
  const link = document.createElement("a");
  link.href = url;
  link.setAttribute("download", filename);
  document.body.appendChild(link);
  link.click();
};

export const useDownloadWill = () => {
  return useGenericMutation(downloadWillPdf, {
    invalidateQueries: false,
    onSuccess: (data) => handleDownload(data, "Last_Will.pdf"),
  });
};

export const useNotifyMutation = () =>
  useGenericMutation(notifyContact, { invalidateQueries: false });

export const useDownloadPoaCare = () => {
  return useGenericMutation(downloadPoaCarePdf, {
    invalidateQueries: false,
    onSuccess: (data) => handleDownload(data, "Poa_Care.pdf"),
  });
};

export const useDownloadPoaProperty = () => {
  return useGenericMutation(downloadPoaPropertyPdf, {
    invalidateQueries: false,
    onSuccess: (data) => handleDownload(data, "Poa_Property.pdf"),
  });
};
